import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import MainContent from '../../../generic-components/Layout/MainContent';
import { useStore } from '../../../store';
import BackIcon from '../../../assets/svg/backArrow.svg';
import moneyMovementIcon from '../../../assets/icons/svg/colored-icons/ic-colored-success.svg';
import UpperMessagePopUp from '../../transactions/components/UpperMessagePopUp';
import { POPUP_LIFESPAN } from '../../emergencyBucketDashboard/constants';
import InfoPopup from '../../dashboard/components/InfoPopup';
import { LiliLogo } from '../../../assets/svg';
import LiliCreditCard from '../../../assets/png/lili-card.png';
import ActivationRenderSwitch from '../components/ActivationRenderSwitch';
import { makeStepBack } from '../helpers/navigateHelper';
import { VIEWS_MAP } from '../../../router/constants';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { CARD_ACTION_LOGIC_ENUMS, FLOW_STEPS } from '../constants';
import { getUrlParamValueByKey, URL_KEYS } from '../../../services/getUrlParamValueByKey';
import { canActivatePhysicalCard, getMostRelevantCard } from '../../../helpers/cardStatus.helper';
import { validateFeatureFlag } from '../../../services/featureFlagService';
import Button from '../../../generic-components/Button';
import Icon from '../../../generic-components/Icon';
import Heading from '../../../generic-components/Heading';
import { Layout } from '../../../components/Layout/Layout';

const ActivateCard = () => {
  const { CARD_TRACKER, LILI_CARD } = VIEWS_MAP;
  const { ENROLL, ENTER_DETAILS } = FLOW_STEPS;
  const { FROM_TRACKER } = URL_KEYS;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getPersonalAccount } = useStore((state) => state);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [step, setStep] = useState<number>(getUrlParamValueByKey(FROM_TRACKER) ? ENTER_DETAILS : ENROLL);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const cardData = getMostRelevantCard(getPersonalAccount().cards);
  const location = useLocation();
  const backDestination = location.state?.backDestination;
  const { refetch: updateSummary } = useAccountSummaryQuery(() => {
    navigate(`/${LILI_CARD}`);
  });

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
  };

  const openSuccessPopup = () => {
    setShowSuccessPopup(true);
  };

  const handleActivatedSuccessfully = () => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, POPUP_LIFESPAN);
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    if (!canActivatePhysicalCard(cardData) || !validateFeatureFlag(CARD_ACTION_LOGIC_ENUMS.ACTIONS.SET_PIN)) {
      navigate(`/${backDestination || VIEWS_MAP.LILI_CARD}`);
    }
  }, []);

  return (
    <Layout wrapperClassName='pt-[78px] pl-[72px] pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <div className='flex items-center gap-[24px] mb-[24px]'>
        <Button
          onClick={() => {
            if (step === 0) {
              makeStepBack(
                getUrlParamValueByKey(FROM_TRACKER) ?? '',
                () => navigate(`/${CARD_TRACKER}`),
                () => navigate(`/${backDestination || LILI_CARD}`),
              );
            } else {
              makeStepBack(
                getUrlParamValueByKey(FROM_TRACKER) ?? '',
                () => navigate(`/${CARD_TRACKER}`),
                () => setStep(step - 1),
              );
            }
          }}
          className=''
          id='backActivateCard'
          childElement={<Icon className='w-[10px]' src={BackIcon} />}
        />
        <Heading title={t('ACTIVATE_CARD_HEADER')} className='font-metricRSemibold text-bw08 text-[20px]' />
      </div>
      <ContentWrapper>
        <MainContent className='flex flex-col gap-4'>
          <div
            className={`flex bg-bw00 rounded-xl w-[744px] ${step === 0 ? 'h-[286px]' : 'h-[496px]'} p-[40px] gap-10`}
          >
            <ActivationRenderSwitch
              openSuccessPopup={openSuccessPopup}
              handleActivatedSuccessfully={handleActivatedSuccessfully}
              stepsObject={{ step, setStep }}
            />
          </div>
          {showPopup && (
            <UpperMessagePopUp
              iconSource={moneyMovementIcon}
              handleClose={closePopup}
              messageText={t('ACTIVATE_CARD_UPPER_MESSAGE')}
            />
          )}
          {showSuccessPopup && (
            <InfoPopup
              popupHeight='h-[504px]'
              className='pt-[50px] pb-[40px] px-[37px]'
              flexParams='flex flex-col items-center'
              handleState={closeSuccessPopup}
              pictureSrc={LiliLogo}
              pictureClassName='w-[48px] h-[32px]'
              picture3ClassName='max-w-[204px] max-h-[145px] mt-[19px] mb-[24.4px]'
              picture3Src={LiliCreditCard}
              showInfoPopup={showSuccessPopup}
              mainButtonText={t('ACTIVATE_CARD_POPUP_BUTTON_TEXT')}
              mainButtonClassName='text-bw00 text-sm font-metricSemibold'
              buttonClassName='h-[50px] w-[160px]'
              text=''
              textClassName='mb-[0]'
              titleClassName='h-[64px] font-metricSemibold mb-[66px] text-[25px] leading-[32.5px] flex-inline text-center items-center whitespace-pre-line'
              title={t('ACTIVATE_CARD_POPUP_MESSAGE')}
              additionalCallback={updateSummary}
            />
          )}
        </MainContent>
      </ContentWrapper>
    </Layout>
  );
};

export default ActivateCard;
